import { useEffect, useMemo, useRef } from 'react';
import type { Block, ComposerDocument } from '../types';
import { RenderBlock } from '../components/renderer';
import { RichEditor } from '../editor/RichEditor';

interface Props {
  doc: ComposerDocument;
  mode: 'edit' | 'preview';
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdateBlockProps: (id: string, patch: Record<string, unknown>) => void;
}

/* The canvas mounts the actual `.sheet` element. Theme + base styles are
   loaded via dynamic <link> tags in <head> so we can swap themes without
   touching the canvas markup. */
function ensureLink(id: string, href: string | null) {
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!href) {
    if (link) link.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.id = id;
    document.head.appendChild(link);
  }
  if (link.href.endsWith(href)) return;
  link.href = href;
}

export function Canvas({ doc, mode, selectedId, onSelect, onUpdateBlockProps }: Props) {
  /* Mount print.css + chosen theme. */
  useEffect(() => {
    ensureLink('composer-base-css', `${import.meta.env.BASE_URL}print.css`);
  }, []);
  useEffect(() => {
    ensureLink('composer-theme-css', doc.theme ? `${import.meta.env.BASE_URL}${doc.theme}` : null);
  }, [doc.theme]);

  const overrideStyle = useMemo(() => {
    const o = doc.colorOverrides || {};
    const s: Record<string, string> = {};
    if (o.accent) s['--accent'] = o.accent;
    if (o.ink) s['--ink'] = o.ink;
    if (o.paper) s['--paper'] = o.paper;
    return s as React.CSSProperties;
  }, [doc.colorOverrides]);

  /* Click on background -> deselect. */
  const sheetRef = useRef<HTMLDivElement>(null);
  const handleSheetClick = (e: React.MouseEvent) => {
    if (mode !== 'edit') return;
    if (e.target === sheetRef.current) onSelect(null);
  };

  const renderRich = (block: Block, key: string, html: string) => (
    <RichEditor
      blockId={`${block.id}:${key}`}
      html={html}
      onChange={(next) => onUpdateBlockProps(block.id, { [key]: next })}
    />
  );

  return (
    <div className={`cb-canvas-wrap mode-${mode}`}>
      <article
        className="sheet cb-canvas"
        ref={sheetRef}
        style={overrideStyle}
        onClick={handleSheetClick}
      >
        {doc.tree.length === 0 && mode === 'edit' && (
          <EmptyHint />
        )}
        {doc.tree.map((b) => (
          <RenderBlock
            key={b.id}
            block={b}
            mode={mode}
            selectedId={selectedId}
            onSelect={onSelect}
            renderRich={renderRich}
          />
        ))}
      </article>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="cb-empty-hint">
      <h2>Empty document</h2>
      <p>
        Use the <strong>Add component</strong> panel on the right to drop your first block.
        Start with a <em>Document Header</em> for a familiar look, or pick anything you like.
      </p>
    </div>
  );
}
